import React, { useState, useEffect } from "react";
import axios from "axios";
import { base_url } from "../utils/axiosConfig";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [id, setId] = useState();

  const fetchOrders = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user_website") || "{}");
      const token = userData.token;
      if (!token) {
        throw new Error("No token found. Please log in.");
      }
      const response = await axios.get(
        `${base_url}product/getOrderForEndUser`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const ordersData = Array.isArray(response.data.data)
        ? response.data.data
        : [];
      console.log("Orders fetched:", ordersData);
      setOrders(ordersData);
      setLoading(false);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to fetch orders",
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const initiatePayment = async ({ id }) => {
    try {
      setLoading(true);

      // 1. Call your backend to create order
      const response = await axios.post(
        `${base_url}cashfree/create-order-EU-repayment`,
        { id },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const { paymentSessionId } = response.data;

      if (!paymentSessionId) {
        throw new Error("No payment session received from server");
      }

      // 3. Start Cashfree checkout
      const cashfree = new window.Cashfree();

      cashfree
        .checkout({
          paymentSessionId,
          redirectTarget: "_self",
          mode: "production", // change to "sandbox" during testing
        })
        .catch((err) => {
          console.error("Cashfree checkout failed:", err);
          toast.error("Failed to open payment gateway. Please try again.");
        });
    } catch (error) {
      console.error("Payment initiation error:", error);

      let errorMessage = "Something went wrong. Please try again.";

      if (error.response) {
        // Server responded with error (4xx, 5xx)
        errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          `Server error (${error.response.status})`;
      } else if (error.request) {
        // No response received (network issue)
        errorMessage = "Network error. Please check your internet connection.";
      } else {
        // Other errors (e.g. invalid JSON, etc.)
        errorMessage = error.message || "Unknown error occurred";
      }

      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const getProductDetails = (product) => {
    let productDetails = null;
    let subCategory = null;
    let subSubCategory = null;
    let subSubSubCategory = null;

    // Safely access subCategories
    if (
      product.productTypeId &&
      Array.isArray(product.productTypeId.subCategories)
    ) {
      subCategory = product.productTypeId.subCategories.find(
        (sub) => sub._id === product.subCategoryId,
      );

      if (subCategory) {
        if (product.subSubSubCategoryId) {
          subSubCategory = (subCategory.subSubCategories || []).find(
            (subSub) => subSub._id === product.subSubCategoryId,
          );
          if (subSubCategory) {
            subSubSubCategory = (subSubCategory.subSubSubCategories || []).find(
              (subSubSub) => subSubSub._id === product.subSubSubCategoryId,
            );
            if (subSubSubCategory) {
              productDetails = (subSubSubCategory.products || []).find(
                (p) => p._id === product.productId,
              );
            }
          }
        } else if (product.subSubCategoryId) {
          subSubCategory = (subCategory.subSubCategories || []).find(
            (subSub) => subSub._id === product.subSubCategoryId,
          );
          if (subSubCategory) {
            productDetails = (subSubCategory.products || []).find(
              (p) => p._id === product.productId,
            );
          }
        } else {
          productDetails = (subCategory.products || []).find(
            (p) => p._id === product.productId,
          );
        }
      }
    }

    let variationDetails = "No variation specified";
    let variationPrice = product.pricePerProduct || 0;
    let productImage =
      productDetails && productDetails.images && productDetails.images[0]
        ? productDetails.images[0]
        : "https://via.placeholder.com/80";

    if (productDetails) {
      if (productDetails.productType === "digital") {
        variationDetails = "Digital Product";
        variationPrice =
          (productDetails.price && productDetails.price.amount) ||
          product.pricePerProduct ||
          0;
      } else if (product.variationId) {
        const variation = (productDetails.variations || []).find(
          (v) => v._id === product.variationId,
        );
        if (variation) {
          variationDetails =
            variation.size || variation.color
              ? `Size: ${variation.size || "N/A"}, Color: ${
                  variation.color || "N/A"
                }`
              : `Default Variant (Price: ₹${
                  (variation.price && variation.price.amount) || "N/A"
                })`;
          variationPrice =
            (variation.price && variation.price.amount) ||
            product.pricePerProduct ||
            0;
          productImage =
            (variation.images && variation.images[0]) || productImage;
        }
      } else if (product.pricePerProduct) {
        const matchingVariation = (productDetails.variations || []).find(
          (v) => v.price && v.price.amount === product.pricePerProduct,
        );
        if (matchingVariation) {
          variationDetails =
            matchingVariation.size || matchingVariation.color
              ? `Size: ${matchingVariation.size || "N/A"}, Color: ${
                  matchingVariation.color || "N/A"
                }`
              : `Default Variant (Price: ₹${
                  (matchingVariation.price && matchingVariation.price.amount) ||
                  "N/A"
                })`;
          variationPrice =
            (matchingVariation.price && matchingVariation.price.amount) ||
            product.pricePerProduct ||
            0;
          productImage =
            (matchingVariation.images && matchingVariation.images[0]) ||
            productImage;
        }
      }
    }

    return {
      productName:
        productDetails && productDetails.productName
          ? productDetails.productName
          : "Unknown Product",
      productImage,
      categoryName:
        product.productTypeId && product.productTypeId.categoryName
          ? product.productTypeId.categoryName
          : "Unknown Category",
      subCategoryName:
        subCategory && subCategory.subCategoryName
          ? subCategory.subCategoryName
          : "Unknown Subcategory",
      variationDetails,
      variationPrice,
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-600" />
          <p className="mt-2 text-lg text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg text-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 text-center sm:text-left text-green-600">
          Your Order History
        </h1>

        {!Array.isArray(orders) || orders.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-lg shadow-md">
            <p className="text-xl text-gray-600 mb-4">
              No orders found. Start shopping now!
            </p>
            <a
              href="/"
              className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
            >
              Browse Products
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {orders.map((order) => (
              <div key={order._id}>
                <div className="bg-white shadow-lg rounded-lg overflow-hidden transform transition-transform hover:scale-[1.02]">
                  <div className="bg-green-100 p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-center">
                      <div className="text-center sm:text-left">
                        <h2 className="text-xl font-bold text-green-600">
                          Order #{order._id ? order._id.slice(-6) : "N/A"}
                        </h2>
                        <p className="text-sm text-gray-600">
                          Placed on{" "}
                          {new Date(
                            order.createdAt || Date.now(),
                          ).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      <div className="flex items-center mt-4 sm:mt-0 gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            order.status === "approved"
                              ? "bg-green-200 text-green-700"
                              : order.status === "paymentPending"
                                ? "bg-yellow-200 text-yellow-700"
                                : "bg-red-200 text-red-700"
                          }`}
                        >
                          {order.status
                            ? order.status.charAt(0).toUpperCase() +
                              order.status.slice(1)
                            : "Unknown"}
                        </span>
                        {order.status === "approved" && order.invoiceUrl && (
                          <a
                            href={order.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                          >
                            Download Invoice
                          </a>
                        )}
                        {order.transaction.status != "PAID" &&
                          !order.transaction.screenshot && (
                            <button
                              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                              onClick={() => initiatePayment({ id: order._id })}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Processing...
                                </>
                              ) : (
                                "Pay Now"
                              )}
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    {(order.transaction ||
                      (order.couponId &&
                        typeof order.couponId === "object" &&
                        order.couponId.couponCode)) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        <div className="flex flex-col">
                          {order.transaction && (
                            <>
                              <p className="text-sm text-gray-600">
                                <strong>Transaction ID:</strong>{" "}
                                {order.transactionId || "N/A"}
                              </p>
                              <p className="text-sm text-gray-600">
                                <strong>Invoice No:</strong>{" "}
                                {order.invoiceNo || ""}
                              </p>
                            </>
                          )}
                          {order.couponId &&
                          typeof order.couponId === "object" &&
                          order.couponId.couponCode ? (
                            <>
                              <p className="text-sm text-gray-600">
                                <strong>Coupon Applied:</strong>{" "}
                                {order.couponId.couponCode}
                              </p>
                              <p className="text-sm text-gray-600">
                                <strong>Discount Type:</strong>{" "}
                                {order.couponId.discountType
                                  ? order.couponId.discountType
                                      .charAt(0)
                                      .toUpperCase() +
                                    order.couponId.discountType.slice(1)
                                  : "N/A"}
                              </p>
                              <p className="text-sm text-gray-600">
                                <strong>Discount Amount:</strong> ₹
                                {(order.discountAmount || 0).toFixed(2)}
                              </p>
                            </>
                          ) : (
                            <p className="text-sm text-gray-600">
                              <strong>Coupon Applied:</strong> None
                            </p>
                          )}
                          <p className="text-sm text-gray-600">
                            <strong>Total (with tax):</strong> ₹
                            {(order.totalPriceWithTax || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                    {(order.transaction ||
                      (order.couponId &&
                        typeof order.couponId === "object" &&
                        order.couponId.couponCode)) && (
                      <hr className="my-6 border-gray-200" />
                    )}
                    <h3 className="text-lg font-semibold mb-4 text-center sm:text-left text-green-600">
                      Items
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {(order.products || []).map((product) => {
                        const {
                          productName,
                          productImage,
                          categoryName,
                          subCategoryName,
                          variationDetails,
                        } = getProductDetails(product);
                        return (
                          <div
                            key={product._id || Math.random()}
                            className="bg-white rounded-lg shadow-md p-4 hover:shadow-xl transition-shadow duration-300"
                          >
                            <div className="flex flex-col items-center">
                              <img
                                src={productImage}
                                alt={productName}
                                style={{
                                  width: "300px",
                                  height: "200px",
                                  objectFit: "cover",
                                  border: "1px solid #dee2e6",
                                }}
                                className="object-cover rounded-lg border-2 border-gray-200 mb-4"
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/80";
                                }}
                              />
                              <h4 className="text-md font-bold text-gray-800 mb-2">
                                {productName}
                              </h4>
                              <p className="text-sm text-gray-600 mb-2">
                                <strong>Category:</strong> {categoryName} -{" "}
                                {subCategoryName}
                              </p>
                              <p className="text-sm text-gray-600 mb-2">
                                <strong>Variation:</strong> {variationDetails}
                              </p>
                              <div className="flex items-center justify-between w-full mt-2">
                                <span className="text-sm text-gray-600">
                                  <strong>Qty:</strong> {product.quantity || 0}
                                </span>
                                <span className="text-md font-semibold text-green-600">
                                  ₹{(product.pricePerProduct || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
